import { types as sdkTypes } from './sdkLoader';
import { getProcess, resolveLatestProcessName } from '../transactions/transaction';
import { parseDateTimeString } from './dates';
import { talkSessionWasRefunded } from './paymentHistory';
import {
  isTalkSessionHoldRelease,
  resolveSessionFormat,
  talkSessionWasCaptured,
} from './sessionPricing';
import { MARKETPLACE_DAY_TZ, nyDateKey, nyPartsFromDate, ymdFromNyDateKey } from './sessionClock';

const { Money } = sdkTypes;

export const RECENT_EARNINGS_PREVIEW = 8;

const WEEKDAY_SUN0 = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const pad2 = n => `${n}`.padStart(2, '0');

const nyWeekdaySun0 = date => {
  const label = date.toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: MARKETPLACE_DAY_TZ,
  });
  return WEEKDAY_SUN0[label] ?? 0;
};

const shiftNyDateKey = (key, deltaDays) => {
  const { year, month, day } = ymdFromNyDateKey(key);
  if (!year || !month || !day) {
    return null;
  }
  const utc = new Date(Date.UTC(year, month - 1, day + deltaDays));
  return `${utc.getUTCFullYear()}-${pad2(utc.getUTCMonth() + 1)}-${pad2(utc.getUTCDate())}`;
};

/**
 * YYYY-MM-DD of Sunday on the America/New_York week that contains `now`.
 *
 * @param {Date} [now]
 * @returns {string|null}
 */
export const startOfNyWeekKey = (now = new Date()) => {
  const key = nyDateKey(now);
  return key ? shiftNyDateKey(key, -nyWeekdaySun0(now)) : null;
};

/**
 * Sunday 00:00 America/New_York for the dashboard This Week card.
 *
 * @param {Date} [now]
 * @returns {Date}
 */
export const startOfLocalWeek = (now = new Date()) => {
  const key = startOfNyWeekKey(now);
  const parsed = key ? parseDateTimeString(`${key} 00:00`, MARKETPLACE_DAY_TZ) : null;
  return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : new Date(now);
};

const EARNED_STATES = new Set([
  'delivered',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
]);

const SKIP_STATES = new Set([
  'canceled',
  'declined',
  'expired',
  'inquiry',
  'pending-payment',
  'preauthorized',
]);

const amountOf = money => {
  const raw = money?.amount;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (raw && typeof raw.toNumber === 'function') {
    return raw.toNumber();
  }
  if (raw != null) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const processStateOf = tx => {
  try {
    return getProcess(resolveLatestProcessName(tx?.attributes?.processName)).getState(tx);
  } catch (e) {
    return null;
  }
};

const saleDate = tx => {
  const booking = tx?.booking?.attributes || {};
  const raw =
    booking.displayStart ||
    booking.start ||
    tx?.attributes?.lastTransitionedAt ||
    tx?.attributes?.createdAt;
  const date = raw ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const lastTransitionLooksCaptured = tx =>
  /charge-after-session|complete|deliver/i.test(tx?.attributes?.lastTransition || '');

const lastTransitionLooksAcceptedHold = tx =>
  /^transition\/(operator-)?accept$/.test(tx?.attributes?.lastTransition || '');

/**
 * Provider payout (preferred) or payin, as a Money value.
 *
 * @param {Object} tx
 * @returns {Object|null}
 */
export const saleMoney = tx => {
  const raw = tx?.attributes?.payoutTotal || tx?.attributes?.payinTotal;
  if (!raw || !raw.currency) {
    return null;
  }
  if (raw instanceof Money) {
    return raw;
  }
  return new Money(amountOf(raw), raw.currency);
};

/**
 * Succeeded tip on this booking (server writes metadata.tip after the charge).
 * listenerSubunits is already the listener's share (SL_TIP_LISTENER_PCT).
 *
 * @param {Object} tx
 * @returns {{ total: Object, earn: Object, pct: number|null, at: Date|null }|null}
 */
export const saleTip = tx => {
  const tip = tx?.attributes?.metadata?.tip;
  const total = Number(tip?.amountSubunits);
  const earn = Number(tip?.listenerSubunits);
  if (tip?.status !== 'succeeded' || !(total > 0) || !(earn >= 0)) {
    return null;
  }
  const currency = String(tip.currency || 'usd').toUpperCase();
  const at = tip.at ? new Date(tip.at) : null;
  return {
    total: new Money(Math.round(total), currency),
    earn: new Money(Math.round(earn), currency),
    pct: Number.isFinite(Number(tip.listenerPct)) ? Number(tip.listenerPct) : null,
    at: at && !Number.isNaN(at.getTime()) ? at : null,
  };
};

/**
 * True when a sale should count toward listener earnings.
 * Talksession holds (accepted, not captured) are pending, not earned.
 *
 * @param {Object} tx
 * @returns {boolean}
 */
export const isCountableSale = tx => {
  const money = saleMoney(tx);
  if (!money) {
    return false;
  }
  const state = processStateOf(tx);
  if (SKIP_STATES.has(state)) {
    return false;
  }
  if (talkSessionWasRefunded(tx) || isTalkSessionHoldRelease(tx)) {
    return false;
  }
  if (talkSessionWasCaptured(tx) || lastTransitionLooksCaptured(tx)) {
    return true;
  }
  return EARNED_STATES.has(state);
};

/**
 * True when an accepted hold is waiting to be captured after the session.
 *
 * @param {Object} tx
 * @returns {boolean}
 */
export const isPendingHold = tx => {
  const money = saleMoney(tx);
  if (!money || isCountableSale(tx)) {
    return false;
  }
  if (talkSessionWasRefunded(tx) || isTalkSessionHoldRelease(tx)) {
    return false;
  }
  const state = processStateOf(tx);
  if (SKIP_STATES.has(state)) {
    return false;
  }
  return state === 'accepted' || lastTransitionLooksAcceptedHold(tx);
};

const toSaleRow = tx => {
  const tip = saleTip(tx);
  const sessionMoney = saleMoney(tx);
  const sessionCounts = isCountableSale(tx) || isPendingHold(tx);
  return {
    id: tx.id.uuid,
    customerName: tx.customer?.attributes?.profile?.displayName || null,
    listingTitle: tx.listing?.attributes?.title || null,
    sessionFormat: resolveSessionFormat(tx?.attributes?.protectedData || {}),
    when: saleDate(tx),
    money: sessionCounts ? sessionMoney : tip ? new Money(0, tip.earn.currency) : sessionMoney,
    tipMoney: tip ? tip.earn : null,
    tipTotal: tip ? tip.total : null,
    // Sweet Treat: the listener gave the free first session unpaid ($0).
    sweetTreat: (() => {
      const pd = tx?.attributes?.protectedData || {};
      const md = tx?.attributes?.metadata || {};
      return pd.sweetTreat === true || pd.listenerFunded === true || md.sweetTreat === true;
    })(),
  };
};

const sortNewestFirst = (a, b) => {
  const aMs = saleDate(a)?.getTime() || 0;
  const bMs = saleDate(b)?.getTime() || 0;
  return bMs - aMs;
};

/**
 * Every countable sale as an earnings line (payoutTotal = listener net).
 *
 * @param {Array<Object>} [sales]
 * @returns {Array<{
 *   id: string,
 *   customerName: string|null,
 *   listingTitle: string|null,
 *   sessionFormat: string|null,
 *   when: Date|null,
 *   money: Object|null
 * }>}
 */
export const listEarnedSales = (sales = []) => {
  return (Array.isArray(sales) ? sales : [])
    .filter(tx => tx?.id?.uuid && (isCountableSale(tx) || saleTip(tx)))
    .sort(sortNewestFirst)
    .map(toSaleRow);
};

/**
 * Upcoming accepted holds, newest session date first.
 *
 * @param {Array<Object>} [sales]
 * @returns {Array}
 */
export const listPendingHolds = (sales = []) => {
  return (Array.isArray(sales) ? sales : [])
    .filter(tx => tx?.id?.uuid && isPendingHold(tx))
    .sort(sortNewestFirst)
    .map(toSaleRow);
};

/**
 * Newest earned sales for the compact Earnings card.
 *
 * @param {Array<Object>} [sales]
 * @returns {Array}
 */
export const listRecentSales = (sales = []) =>
  listEarnedSales(sales).slice(0, RECENT_EARNINGS_PREVIEW);

/**
 * Sum provider payouts (payoutTotal) for this NY week, month, YTD, and all time.
 *
 * @param {Array<Object>} [sales]
 * @param {Date} [now]
 * @returns {{
 *   currency: string|null,
 *   week: Object|null,
 *   month: Object|null,
 *   ytd: Object|null,
 *   allTime: Object|null,
 *   weekCount: number,
 *   monthCount: number,
 *   ytdCount: number,
 *   allCount: number
 * }}
 */
export const summarizeListenerEarnings = (sales = [], now = new Date()) => {
  let currency = null;
  let weekAmount = 0;
  let monthAmount = 0;
  let ytdAmount = 0;
  let allAmount = 0;
  let weekCount = 0;
  let monthCount = 0;
  let ytdCount = 0;
  let allCount = 0;
  const weekStartKey = startOfNyWeekKey(now);
  const nowParts = nyPartsFromDate(now);

  const weekEndKey = weekStartKey ? shiftNyDateKey(weekStartKey, 7) : null;
  const add = (amount, when, countSession) => {
    allAmount += amount;
    allCount += countSession ? 1 : 0;
    const whenKey = when ? nyDateKey(when) : null;
    const inWeek =
      whenKey && weekStartKey && weekEndKey && whenKey >= weekStartKey && whenKey < weekEndKey;
    if (inWeek) {
      weekAmount += amount;
      weekCount += countSession ? 1 : 0;
    }
    if (whenKey) {
      const parts = ymdFromNyDateKey(whenKey);
      if (parts.year === nowParts.year && parts.month === nowParts.month) {
        monthAmount += amount;
        monthCount += countSession ? 1 : 0;
      }
      if (parts.year === nowParts.year) {
        ytdAmount += amount;
        ytdCount += countSession ? 1 : 0;
      }
    }
  };

  (Array.isArray(sales) ? sales : []).forEach(tx => {
    const money = isCountableSale(tx) ? saleMoney(tx) : null;
    if (money && (!currency || money.currency === currency)) {
      currency = money.currency;
      add(amountOf(money), saleDate(tx), true);
    }
    // Tips: the listener share counts as earnings (dated when tipped).
    const tip = saleTip(tx);
    if (tip && (!currency || tip.earn.currency === currency)) {
      currency = tip.earn.currency;
      add(amountOf(tip.earn), tip.at || saleDate(tx), false);
    }
  });

  return {
    currency,
    week: currency ? new Money(weekAmount, currency) : null,
    month: currency ? new Money(monthAmount, currency) : null,
    ytd: currency ? new Money(ytdAmount, currency) : null,
    allTime: currency ? new Money(allAmount, currency) : null,
    weekCount,
    monthCount,
    ytdCount,
    allCount,
  };
};
